import { useMemo } from "react";
import { errorMessage, useMonitoringMetrics, useMonitoringPipeline } from "../api";
import { ErrorBlock, LoadingBlock } from "../components/primitives";
import { BranchesCard } from "./pipeline/branches";
import { FlowChartCard, LatencyCard } from "./pipeline/flow";
import { FunnelCard } from "./pipeline/funnel";
import { branchLookup } from "./pipeline/helpers";
import { RecentCard } from "./pipeline/recent";
import { EvaluationCharts, StepsCard } from "./pipeline/steps";
import { StuckCard, UncapturedCard } from "./pipeline/stuck";
import { PipelineTotals } from "./pipeline/totals";

/**
 * Pizza flow: orders → packed → captured → uploaded → evaluated over the board window, from the business
 * data (pizza / order) plus the in-process metrics; what got stuck where, per branch, and how long it took.
 */
export function PipelineTab() {
  const pipeline = useMonitoringPipeline();
  const metrics = useMonitoringMetrics();
  const data = pipeline.data;
  const branchOf = useMemo(() => branchLookup(data?.branches_meta), [data?.branches_meta]);
  const retry = () => void pipeline.refetch();

  if (!data) {
    return (
      <div className="flex min-w-0 flex-col gap-4">
        {pipeline.isError ? (
          <ErrorBlock message={errorMessage(pipeline.error)} onRetry={retry} />
        ) : (
          <>
            <LoadingBlock height={84} />
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <LoadingBlock height={300} />
              <LoadingBlock height={300} />
            </div>
          </>
        )}
        <EvaluationCharts query={metrics} />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {pipeline.isError && <ErrorBlock message={errorMessage(pipeline.error)} onRetry={retry} />}
      <PipelineTotals data={data} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <FlowChartCard data={data} />
        <LatencyCard data={data} />
      </div>
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
        <StuckCard data={data} branchOf={branchOf} />
        <UncapturedCard data={data} branchOf={branchOf} />
      </div>
      <FunnelCard data={data} branchOf={branchOf} />
      <BranchesCard data={data} />
      <StepsCard data={data} />
      <EvaluationCharts query={metrics} />
      <RecentCard data={data} branchOf={branchOf} />
    </div>
  );
}
